'use client'

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
// Using regular img tag instead of Next.js Image component
// Backend API imports - using custom backend
import ApplicationForm from '@/components/client/ApplicationForm'
import SimilarProperties from '@/components/client/SimilarProperties'
import { useCurrency } from '@/lib/currency'
import { PropertyWithLandlord } from '@/types'
import { getApiUrl } from '@/lib/apiUrl'
import { useAuth } from '@/context/AuthContext'

// Payment Countdown Component
function PaymentCountdown({ expiresAt }: { expiresAt: string | Date }) {
  const [timeRemaining, setTimeRemaining] = useState(() => {
    const expiration = new Date(expiresAt)
    const now = new Date()
    const diff = expiration.getTime() - now.getTime()

    if (diff <= 0) {
      return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    return { expired: false, days, hours, minutes, seconds }
  })

  useEffect(() => {
    const interval = setInterval(() => {
      const expiration = new Date(expiresAt)
      const now = new Date()
      const diff = expiration.getTime() - now.getTime()

      if (diff <= 0) {
        setTimeRemaining({ expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 })
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeRemaining({ expired: false, days, hours, minutes, seconds })
    }, 1000)

    return () => clearInterval(interval)
  }, [expiresAt])

  if (timeRemaining.expired) {
    return (
      <div className="text-red-600 font-semibold">
        ⚠️ Escrow period has expired
      </div>
    )
  }

  return (
    <div className="flex items-center space-x-4">
      {timeRemaining.days > 0 && (
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{timeRemaining.days}</div>
          <div className="text-xs text-gray-600">Days</div>
        </div>
      )}
      <div className="text-center">
        <div className="text-2xl font-bold text-blue-600">{timeRemaining.hours}</div>
        <div className="text-xs text-gray-600">Hours</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-blue-600">{timeRemaining.minutes}</div>
        <div className="text-xs text-gray-600">Minutes</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-blue-600">{timeRemaining.seconds}</div>
        <div className="text-xs text-gray-600">Seconds</div>
      </div>
    </div>
  )
}

export default function PropertyDetailPage() {
  const { format } = useCurrency()
  const { user } = useAuth()
  // Backend API calls will be implemented
  const params = useParams()
  const navigate = useNavigate()
  const [property, setProperty] = useState<PropertyWithLandlord | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isSaved, setIsSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const [showApplicationForm, setShowApplicationForm] = useState(false)
  const [payment, setPayment] = useState<any>(null)
  const [loadingPayment, setLoadingPayment] = useState(false)

  // Navigation functions for image gallery
  const nextImage = useCallback(() => {
    if (property?.images && currentImageIndex < property.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
    }
  }, [currentImageIndex, property?.images])

  const prevImage = useCallback(() => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
    }
  }, [currentImageIndex])

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      nextImage()
    }
    if (isRightSwipe) {
      prevImage()
    }
  }

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        prevImage()
      } else if (event.key === 'ArrowRight') {
        nextImage()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentImageIndex, property?.images, nextImage, prevImage])

  useEffect(() => {
    const loadProperty = async () => {
      if (!params.id) return

      setLoading(true)
      try {
        const apiUrl = getApiUrl()
        const token = localStorage.getItem('authToken')
        
        console.log('🔍 Loading property:', params.id, 'from:', apiUrl)
        
        const response = await fetch(`${apiUrl}/properties/${params.id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        })

        console.log('📡 Property fetch response:', response.status, response.statusText)

        if (!response.ok) {
          if (response.status === 404) {
            console.error('❌ Property not found:', params.id)
            // Try to get error message from response
            try {
              const errorData = await response.json()
              console.error('Error details:', errorData)
            } catch {
              console.error('No error details in response')
            }
            setProperty(null)
            setLoading(false)
            return
          }
          
          // Try to get error message
          let errorMessage = `Failed to load property: ${response.statusText}`
          try {
            const errorData = await response.json()
            errorMessage = errorData.message || errorMessage
            console.error('Error response:', errorData)
          } catch {
            // Response is not JSON
          }
          
          throw new Error(errorMessage)
        }

        const data = await response.json()
        console.log('✅ Property details loaded:', data)
        
        // Transform property data to match frontend format
        const propertyData = data.property || data
        if (!propertyData) {
          console.error('No property data in response')
          setProperty(null)
          setLoading(false)
          return
        }

        const transformedProperty: PropertyWithLandlord = {
          ...propertyData,
          id: propertyData._id || propertyData.id,
          // Extract and normalize image URLs
          images: (() => {
            const imageUrls = propertyData.images?.map((img: any) => 
              typeof img === 'string' ? img : img.url
            ) || propertyData.images || [];
            // Normalize localhost URLs to use backend URL from environment
            return imageUrls.map((url: string) => {
              if (!url) return url;
              if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
                const apiUrl = getApiUrl();
                const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
                try {
                  const urlObj = new URL(url);
                  return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
                } catch {
                  return url.replace(/https?:\/\/[^/]+/, apiBaseUrl);
                }
              } else if (url.startsWith('/uploads')) {
                const apiUrl = getApiUrl();
                const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
                return `${apiBaseUrl}${url}`;
              }
              return url;
            });
          })(),
          // Build location from address
          location: propertyData.location || (propertyData.address && typeof propertyData.address === 'object' 
            ? [propertyData.address.street, propertyData.address.city, propertyData.address.state].filter(Boolean).join(', ')
            : 'Location not specified'),
          // Map verification status
          is_verified: propertyData.isVerified !== undefined ? propertyData.isVerified : propertyData.is_verified,
          is_available: propertyData.isAvailable !== undefined ? propertyData.isAvailable : propertyData.is_available,
          created_at: propertyData.createdAt || propertyData.created_at,
          // Transform landlord data
          landlord: propertyData.landlord ? {
            ...propertyData.landlord,
            id: propertyData.landlord._id || propertyData.landlord.id
          } : propertyData.landlord,
          // Extract house documents if present
          house_documents: propertyData.houseDocuments?.map((doc: any) => 
            typeof doc === 'string' ? doc : doc.url
          ) || propertyData.house_documents || [],
          // Extract videos if present
          videos: propertyData.videos?.map((video: any) => 
            typeof video === 'string' ? video : video.url
          ) || propertyData.videos || []
        }

        console.log('✅ Transformed property:', transformedProperty)
        setProperty(transformedProperty)
      } catch (error) {
        console.error('❌ Error loading property:', error)
        setProperty(null)
      } finally {
        setLoading(false)
      }
    }

    const checkIfSaved = async () => {
      try {
        if (!params.id) return
        const token = localStorage.getItem('authToken')
        if (!token) return // Not logged in: treat as not saved

        const apiUrl = getApiUrl()
        const response = await fetch(`${apiUrl}/users/saved-properties/${params.id}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) return
        const data = await response.json()
        setIsSaved(!!data?.isSaved)
      } catch (error) {
        console.error('Error checking saved status:', error)
      }
    }

    loadProperty()
    checkIfSaved()
  }, [params.id, navigate])

  // Check for accepted application and payment status
  useEffect(() => {
    const checkApplicationAndPayment = async () => {
      if (!property?.id || !user) return

      setLoadingPayment(true)
      try {
        const token = localStorage.getItem('authToken')
        if (!token) {
          setLoadingPayment(false)
          return
        }

        const apiUrl = getApiUrl()
        
        // Fetch user's applications (fetch all and filter for accepted/approved)
        const applicationsResponse = await fetch(`${apiUrl}/applications`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })

        if (applicationsResponse.ok) {
          const applicationsData = await applicationsResponse.json()
          const allApplications = applicationsData.applications || []
          
          // Find accepted or approved application for this property
          const propertyApplication = allApplications.find((app: any) => {
            const appPropertyId = app.property?._id || app.property?.id
            const currentPropertyId = property.id || property._id
            const matchesProperty = appPropertyId === currentPropertyId || appPropertyId?.toString() === currentPropertyId?.toString()
            const isAcceptedOrApproved = app.status === 'accepted' || app.status === 'approved'
            return matchesProperty && isAcceptedOrApproved
          })

          if (propertyApplication) {
            // Check payment status for this application
            const paymentResponse = await fetch(`${apiUrl}/payments/history?applicationId=${propertyApplication._id || propertyApplication.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            })

            if (paymentResponse.ok) {
              const paymentData = await paymentResponse.json()
              const payments = paymentData.payments || []
              const completedPayment = payments.find((p: any) => 
                p.status === 'completed' || p.status === 'succeeded'
              )

              if (completedPayment) {
                setPayment(completedPayment)
              }
            }
          }
        }
      } catch (error) {
        console.error('Error checking application and payment:', error)
      } finally {
        setLoadingPayment(false)
      }
    }

    if (property && user) {
      checkApplicationAndPayment()
    }
  }, [property?.id, user])

  // Format price via currency context
  const formatPrice = (price: number) => format(price)

  // Get landlord display name (currently unused but may be needed)
  // const getLandlordDisplayName = (landlord: User | null | undefined) => {
  //   if (!landlord) return 'Unknown Landlord'
  //   if (landlord.kyc_data?.firstName && landlord.kyc_data?.lastName) {
  //     return `${landlord.kyc_data.firstName} ${landlord.kyc_data.lastName}`
  //   }
  //   return landlord.email || 'Verified Landlord'
  // }

  // Handle save/unsave property
  const handleToggleSaved = async () => {
    if (!property) return

    setSaving(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        alert('Please log in to save properties')
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/users/saved-properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          propertyId: property.id,
          action: isSaved ? 'unsave' : 'save'
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        alert(errorData.message || 'Failed to update saved property')
        return
      }

      setIsSaved(!isSaved)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    )
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h1>
          <p className="text-gray-600 mb-6">The property you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <button
            onClick={() => navigate('/dashboard/client')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <button
                onClick={() => navigate('/dashboard/client')}
                className="text-blue-600 hover:text-blue-700 mb-2 flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">{property.title}</h1>
              <p className="text-gray-600">{property.location}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-green-600">{formatPrice(property.price)}</p>
              <p className="text-sm text-gray-500">
                {property.rentalType === 'short-term' ? 'per day' : 'per month'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
                         {/* Image Gallery */}
             <div className="bg-white rounded-lg shadow overflow-hidden">
               <div 
                 className="relative aspect-[4/3] min-h-[384px] max-h-[600px] cursor-grab active:cursor-grabbing image-gallery-container image-gallery-main"
                 onTouchStart={handleTouchStart}
                 onTouchMove={handleTouchMove}
                 onTouchEnd={handleTouchEnd}
               >
                 {property.images && property.images.length > 0 ? (
                   <>
                     <img
                       src={property.images[currentImageIndex]}
                       alt={`${property.title} - Image ${currentImageIndex + 1}`}
                       className="w-full h-full object-contain sm:object-cover transition-all duration-300 ease-in-out select-none"
                       onError={(e) => {
                         e.currentTarget.src = '/placeholder-property.svg'
                       }}
                       draggable={false}
                     />
                     
                     {/* Save Button */}
                     <button
                       onClick={handleToggleSaved}
                       disabled={saving}
                       className="absolute top-4 right-4 p-3 rounded-full bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200 shadow-md hover:shadow-lg z-10"
                       title={isSaved ? 'Remove from saved' : 'Save property'}
                     >
                       {saving ? (
                         <svg className="w-6 h-6 animate-spin text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                         </svg>
                       ) : isSaved ? (
                         <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                           <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                         </svg>
                       ) : (
                         <svg className="w-6 h-6 text-gray-600 hover:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                         </svg>
                       )}
                     </button>

                     {/* Navigation Arrows */}
                     {property.images.length > 1 && (
                       <>
                         {/* Previous Button */}
                         <button
                           onClick={prevImage}
                           disabled={currentImageIndex === 0}
                           className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed z-10"
                           title="Previous image"
                         >
                           <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                           </svg>
                         </button>

                         {/* Next Button */}
                         <button
                           onClick={nextImage}
                           disabled={currentImageIndex === property.images.length - 1}
                           className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white bg-opacity-90 hover:bg-opacity-100 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed z-10"
                           title="Next image"
                         >
                           <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                           </svg>
                         </button>

                         {/* Image Counter */}
                         <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
                           {currentImageIndex + 1} / {property.images.length}
                         </div>
                       </>
                     )}

                     {/* Image Dots */}
                     {property.images.length > 1 && (
                       <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                         {property.images.map((_, index) => (
                           <button
                             key={index}
                             onClick={() => setCurrentImageIndex(index)}
                             className={`w-3 h-3 rounded-full transition-all duration-200 ${
                               index === currentImageIndex 
                                 ? 'bg-white shadow-lg' 
                                 : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                             }`}
                             title={`Go to image ${index + 1}`}
                           />
                         ))}
                       </div>
                     )}
                   </>
                 ) : (
                   <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                     <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                     </svg>
                   </div>
                 )}
               </div>

               {/* Thumbnail Strip */}
               {property.images && property.images.length > 1 && (
                 <div className="p-4 bg-gray-50">
                   <div className="flex space-x-2 overflow-x-auto pb-2">
                     {property.images.map((image, index) => (
                       <button
                         key={index}
                         onClick={() => setCurrentImageIndex(index)}
                         className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                           index === currentImageIndex
                             ? 'border-blue-500 shadow-lg'
                             : 'border-gray-200 hover:border-gray-300'
                         }`}
                         title={`View image ${index + 1}`}
                       >
                         <img
                           src={image}
                           alt={`${property.title} thumbnail ${index + 1}`}
                           className="w-full h-full object-cover"
                           onError={(e) => {
                             e.currentTarget.src = '/placeholder-property.svg'
                           }}
                         />
                       </button>
                     ))}
                   </div>
                 </div>
               )}
             </div>

            {/* Property Videos */}
            {property.videos && property.videos.length > 0 && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Property Videos</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property.videos.map((video, index) => (
                      <div key={index} className="relative">
                        <video
                          src={video}
                          className="w-full h-64 object-cover rounded-lg border"
                          controls
                          preload="metadata"
                        />
                        <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          Video {index + 1}
                        </div>
                        {index === 0 && (
                          <div className="absolute top-2 left-2 bg-purple-500 text-white text-xs px-2 py-1 rounded">
                            Featured
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-3">
                    🎥 {property.videos.length} video{property.videos.length > 1 ? 's' : ''} available
                  </p>
                </div>
              </div>
            )}

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Property Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700 leading-relaxed">{property.description}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Key Features</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Rental Type: {property.rentalType === 'short-term' ? 'Short-term (Shortlet)' : 'Long-term'}
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Property Type: {(property as any).propertyType || property.property_type ? 
                        ((property as any).propertyType || property.property_type).charAt(0).toUpperCase() + 
                        ((property as any).propertyType || property.property_type).slice(1).replace(/_/g, ' ') : 'N/A'}
                    </li>
                    {property.bedrooms && (
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Bedrooms: {property.bedrooms}
                      </li>
                    )}
                    {property.bathrooms && (
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        Bathrooms: {property.bathrooms}
                      </li>
                    )}
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Availability: {property.is_available ? 'Available' : 'Not Available'}
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Verified Property
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      {property.images?.length || 0} Photos Available
                    </li>
                    {property.videos && property.videos.length > 0 && (
                      <li className="flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        {property.videos.length} Video{property.videos.length > 1 ? 's' : ''} Available
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

                         {/* Landlord Information */}
             {/* <div className="bg-white rounded-lg shadow p-6">
               <h2 className="text-xl font-semibold text-gray-900 mb-4">Landlord Information</h2>
               <div className="flex items-center justify-between">
                 <div>
                   <h3 className="font-medium text-gray-900">{getLandlordDisplayName(property.landlord)}</h3>
                   <p className="text-gray-600">{property.landlord?.email || 'No email available'}</p>
                   <div className="mt-2">
                     {property.landlord && <VerificationBadge user={property.landlord} size="sm" />}
                   </div>
                 </div>
                 {(() => {
                   const landlordEmail = property.landlord?.email
                   const subject = encodeURIComponent(`Inquiry about ${property.title}`)
                   const body = encodeURIComponent(`Hello,\n\nI'm interested in ${property.title} located at ${property.location}.\n\n`)
                   const href = landlordEmail
                     ? `mailto:${landlordEmail}?subject=${subject}&body=${body}`
                     : undefined
                   return (
                     <button
                       onClick={() => href && (window.location.href = href)}
                       disabled={!href}
                       title={!href ? 'Landlord email not available' : 'Email landlord'}
                       className={`px-4 py-2 rounded-md transition-colors ${
                         href ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-400 text-white cursor-not-allowed'
                       }`}
                     >
                       Contact Landlord
                     </button>
                   )
                 })()}
               </div>
             </div> */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
                         {/* Quick Actions */}
             <div className="bg-white rounded-lg shadow p-6">
               <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
               <div className="space-y-3">
                      {payment && payment.status === 'completed' ? (
                        <div className="w-full py-3 px-4 rounded-md bg-emerald-600 text-white">
                          <div className="flex items-center justify-center space-x-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-semibold">Payment Confirmed</span>
                          </div>
                          {payment.escrowStatus === 'held' && (
                            <div className="mt-2 text-sm text-emerald-100 text-center">
                              Payment held in escrow
                            </div>
                          )}
                        </div>
                      ) : (
                        <button 
                          onClick={() => user?.is_verified && setShowApplicationForm(true)}
                          disabled={!user?.is_verified || loadingPayment}
                          title={!user?.is_verified ? 'Complete KYC verification to apply' : ''}
                          className={`w-full py-3 px-4 rounded-md transition-colors font-medium ${
                            user?.is_verified 
                              ? 'bg-green-600 text-white hover:bg-green-700' 
                              : 'bg-gray-400 text-white cursor-not-allowed'
                          }`}
                        >
                          {user?.is_verified ? 'Inquire Now' : 'KYC Required'}
                        </button>
                      )}
                 <button 
                   onClick={handleToggleSaved}
                   disabled={saving}
                   className={`w-full py-3 px-4 rounded-md transition-colors font-medium flex items-center justify-center space-x-2 ${
                     isSaved 
                       ? 'bg-red-600 text-white hover:bg-red-700' 
                       : 'bg-purple-600 text-white hover:bg-purple-700'
                   }`}
                 >
                   {saving ? (
                     <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                     </svg>
                   ) : isSaved ? (
                     <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                       <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                     </svg>
                   ) : (
                     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                     </svg>
                   )}
                   <span>{isSaved ? 'Remove from Saved' : 'Save Property'}</span>
                 </button>
                  {/* {(() => {
                    const landlordEmail = property.landlord?.email
                    const subject = encodeURIComponent(`Inquiry about ${property.title}`)
                    const body = encodeURIComponent(`Hello,\n\nI'm interested in ${property.title} located at ${property.location}.\n\n`)
                    const href = landlordEmail
                      ? `mailto:${landlordEmail}?subject=${subject}&body=${body}`
                      : `mailto:support@landlordnoagent.com?subject=${subject}&body=${body}`
                    const disabled = !landlordEmail
                    return (
                      <a
                        href={href}
                        className={`w-full inline-flex items-center justify-center space-x-2 py-3 px-4 rounded-md transition-colors font-medium ${
                          disabled ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        title={disabled ? 'Landlord email not available; contacting support instead' : 'Email landlord'}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12H8m8 0a4 4 0 10-8 0 4 4 0 008 0z" />
                        </svg>
                        <span>{landlordEmail ? 'Email Landlord' : 'Email Support'}</span>
                      </a>
                    )
                  })()} */}
               </div>
             </div>

            {/* Property Stats */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Price</span>
                  <span className="font-semibold text-green-600">
                    {formatPrice(property.price)} {property.rentalType === 'short-term' ? '/day' : '/month'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rental Type</span>
                  <span className="font-semibold">
                    {property.rentalType === 'short-term' ? 'Short-term (Shortlet)' : 'Long-term'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Property Type</span>
                  <span className="font-semibold">
                    {(property as any).propertyType || property.property_type ? 
                      ((property as any).propertyType || property.property_type).charAt(0).toUpperCase() + 
                      ((property as any).propertyType || property.property_type).slice(1).replace(/_/g, ' ') : 'N/A'}
                  </span>
                </div>
                {property.bedrooms && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bedrooms</span>
                    <span className="font-semibold">{property.bedrooms}</span>
                  </div>
                )}
                {property.bathrooms && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bathrooms</span>
                    <span className="font-semibold">{property.bathrooms}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Availability</span>
                  <span className={`font-semibold ${property.is_available ? 'text-green-600' : 'text-red-600'}`}>
                    {property.is_available ? 'Available' : 'Not Available'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="font-semibold text-green-600">✓ Verified</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Photos</span>
                  <span className="font-semibold">{property.images?.length || 0}</span>
                </div>
              </div>
            </div>

            {/* Payment Information - Show when payment is confirmed */}
            {payment && payment.status === 'completed' && (
              <div className="bg-white rounded-lg shadow p-6 border-l-4 border-emerald-500">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Payment Information</span>
                </h3>
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-medium">Payment Status</span>
                      <span className="px-3 py-1 bg-emerald-600 text-white rounded-full text-sm font-semibold">
                        ✓ Paid
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <span className="text-gray-600 text-sm">Amount Paid</span>
                        <p className="font-semibold text-gray-900">{formatPrice(payment.amount)} {payment.currency?.toUpperCase() || 'NGN'}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Payment Date</span>
                        <p className="font-semibold text-gray-900">
                          {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {payment.isEscrow && payment.escrowStatus === 'held' && payment.escrowExpiresAt && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-700 font-medium flex items-center space-x-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Escrow Expiration</span>
                        </span>
                      </div>
                      <PaymentCountdown expiresAt={payment.escrowExpiresAt} />
                      <p className="text-sm text-gray-600 mt-2">
                        Your payment is securely held in escrow. Complete property visit and document verification within this time to avoid additional charges.
                      </p>
                    </div>
                  )}

                  {payment.escrowStatus === 'released' && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium text-green-800">Escrow Released</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Payment has been released to the landlord.
                      </p>
                    </div>
                  )}

                  {payment.propertyVisited !== undefined && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Property Visited</span>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        payment.propertyVisited 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {payment.propertyVisited ? 'Yes' : 'Pending'}
                      </span>
                    </div>
                  )}

                  {payment.documentsReceived !== undefined && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Documents Received</span>
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        payment.documentsReceived 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {payment.documentsReceived ? 'Yes' : 'Pending'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address Details */}
            {property.address && typeof property.address === 'object' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                <div className="space-y-2 text-gray-700">
                  {(property.address as any).street && (
                    <p><span className="font-medium">Street:</span> {(property.address as any).street}</p>
                  )}
                  {(property.address as any).city && (
                    <p><span className="font-medium">City:</span> {(property.address as any).city}</p>
                  )}
                  {(property.address as any).state && (
                    <p><span className="font-medium">State:</span> {(property.address as any).state}</p>
                  )}
                  {(property.address as any).zipCode && (
                    <p><span className="font-medium">Zip Code:</span> {(property.address as any).zipCode}</p>
                  )}
                  {(property.address as any).country && (
                    <p><span className="font-medium">Country:</span> {(property.address as any).country}</p>
                  )}
                </div>
              </div>
            )}

            {/* Features & Amenities */}
            {((property as any).features && (property as any).features.length > 0) || (property.amenities && property.amenities.length > 0) ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Features & Amenities</h3>
                {(property as any).features && (property as any).features.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Features</h4>
                    <div className="flex flex-wrap gap-2">
                      {(property as any).features.map((feature: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {feature.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {property.amenities && property.amenities.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Amenities</h4>
                    <div className="flex flex-wrap gap-2">
                      {property.amenities.map((amenity: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                        >
                          {amenity.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* Available Dates for Short-term Rentals */}
            {property.rentalType === 'short-term' && (property as any).availableDates && (property as any).availableDates.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Dates</h3>
                <div className="space-y-2">
                  {(property as any).availableDates.map((dateRange: any, index: number) => {
                    const startDate = new Date(dateRange.start || dateRange.startDate)
                    const endDate = new Date(dateRange.end || dateRange.endDate)
                    return (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">From:</span> {startDate.toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">To:</span> {endDate.toLocaleDateString()}
                        </p>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Similar Properties */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Similar Properties</h3>
              <SimilarProperties currentProperty={property} />
            </div>
          </div>
        </div>
      </div>
      {showApplicationForm && property && (
        <ApplicationForm
          property={property}
          isOpen={showApplicationForm}
          onCancel={() => setShowApplicationForm(false)}
          onSuccess={() => {
            setShowApplicationForm(false)
            navigate('/dashboard/client')
          }}
        />
      )}
    </div>
  )
} 